/**
 * @fileoverview QR Code Utilities - QR code generation and scanning for device linking
 *
 * This service provides QR code-based device linking capabilities for WebAuthn
 * cross-device authentication. Implements the WhatsApp-style device linking flow
 * where secondary devices scan a QR code from the primary device to establish
 * a secure connection.
 *
 * **QR Code Flow:**
 * 1. Desktop shows QR code containing challenge + session ID
 * 2. Mobile scans QR code
 * 3. Mobile authenticates with WebAuthn
 * 4. Mobile establishes encrypted channel with desktop
 * 5. Desktop receives authentication confirmation
 *
 * **Security:**
 * - Short-lived QR codes (5 minute expiration)
 * - Cryptographic challenge prevents replay attacks
 * - Encrypted WebSocket channel for device communication
 * - QR codes contain no sensitive data
 *
 * @module services/QRCodeService
 * @requires @xanectech/lib
 */

import { createLogger } from '@xanectech/lib';
import type {
  QRCodePayload,
  QRCodeOptions,
  ScanResult,
} from '@xanectech/types';

const logger = createLogger('QRCodeService');

// ============================================================================
// QR Code Generation
// ============================================================================

/**
 * Generate QR code data for device linking
 *
 * Creates a QR code payload containing all necessary information for a
 * secondary device to establish a secure connection with this device.
 *
 * @param userId - User ID
 * @param deviceId - Current device ID
 * @param action - Action type (link-device or authenticate)
 * @param expirationMinutes - QR code expiration time (default: 5 minutes)
 * @returns QR code payload
 */
export async function generateDeviceLinkingPayload(
  userId: string,
  deviceId: string,
  action: 'link-device' | 'authenticate',
  expirationMinutes: number = 5,
): Promise<QRCodePayload> {
  try {
    // Generate cryptographic challenge
    const challenge = await generateChallenge();

    // Generate session ID
    const sessionId = generateSessionId();

    // Calculate timestamps
    const timestamp = Date.now();
    const expiresAt = timestamp + expirationMinutes * 60 * 1000;

    // Get server URL from environment or window location
    const serverUrl = getServerUrl();

    const payload: QRCodePayload = {
      version: '1.0',
      action,
      userId,
      deviceId,
      challenge,
      sessionId,
      timestamp,
      expiresAt,
      serverUrl,
    };

    return payload;
  } catch (error) {
    logger.error('Failed to generate QR code payload', error as Error);
    throw new Error('Failed to generate QR code');
  }
}

/**
 * Generate QR code image as Data URL
 *
 * Creates a scannable QR code image from the payload data.
 * Uses a lightweight QR code library or generates manually.
 *
 * @param payload - QR code payload
 * @param options - QR code generation options
 * @returns Data URL of QR code image
 */
export async function generateQRCodeImage(
  payload: QRCodePayload,
  options: QRCodeOptions = {},
): Promise<string> {
  try {
    // Serialize payload to JSON string
    const payloadString = JSON.stringify(payload);

    // For MVP, return a placeholder data URL
    // In production, use a QR code library like qrcode or qr-code-styling
    const canvas = document.createElement('canvas');
    const size = options.size || 300;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    // Draw placeholder QR code pattern
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);

    ctx.fillStyle = '#000000';
    // Simple pattern (production would use actual QR code generation)
    const cellSize = size / 10;
    for (let i = 0; i < 10; i++) {
      for (let j = 0; j < 10; j++) {
        if ((i + j) % 2 === 0) {
          ctx.fillRect(i * cellSize, j * cellSize, cellSize, cellSize);
        }
      }
    }

    // Add text overlay (for debugging - remove in production)
    ctx.fillStyle = '#666666';
    ctx.font = '12px monospace';
    ctx.textAlign = 'center';
    const centerX = size / 2;
    const centerY = size / 2;
    ctx.fillText('Scan with mobile', centerX, centerY - 10);
    ctx.fillText(
      String(payload.sessionId.substring(0, 8)),
      centerX,
      centerY + 10,
    );

    // Convert to data URL
    const dataUrl = canvas.toDataURL('image/png');

    // Store payload for later validation
    storeQRCodePayload(String(payload.sessionId), payloadString);

    return dataUrl;
  } catch (error) {
    logger.error('Failed to generate QR code image', error as Error);
    throw new Error('Failed to generate QR code image');
  }
}

// ============================================================================
// QR Code Scanning
// ============================================================================

/**
 * Parse scanned QR code data
 *
 * @param qrCodeData - Raw QR code data string
 * @returns Parsed and validated payload
 */
export function parseQRCodeData(qrCodeData: string): ScanResult {
  try {
    // Parse JSON payload
    const payload = JSON.parse(qrCodeData) as QRCodePayload;

    // Validate payload structure
    if (!isValidPayload(payload)) {
      return {
        payload: payload,
        isValid: false,
        error: 'Invalid QR code format',
      };
    }

    // Check expiration
    if (payload.expiresAt < Date.now()) {
      return {
        payload: payload,
        isValid: false,
        error: 'QR code has expired',
      };
    }

    // Check version compatibility
    if (payload.version !== '1.0') {
      return {
        payload: payload,
        isValid: false,
        error: 'Unsupported QR code version',
      };
    }

    return {
      payload: payload,
      isValid: true,
    };
  } catch (error) {
    logger.error('Failed to parse QR code', error as Error);
    return {
      payload: {} as QRCodePayload,
      isValid: false,
      error: 'Invalid QR code data',
    };
  }
}

/**
 * Validate QR code payload structure
 */
function isValidPayload(payload: unknown): payload is QRCodePayload {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const candidate = payload as Record<string, unknown>;

  return (
    typeof candidate.version === 'string' &&
    (candidate.action === 'link-device' ||
      candidate.action === 'authenticate') &&
    typeof candidate.userId === 'string' &&
    typeof candidate.deviceId === 'string' &&
    typeof candidate.challenge === 'string' &&
    typeof candidate.sessionId === 'string' &&
    typeof candidate.timestamp === 'number' &&
    typeof candidate.expiresAt === 'number' &&
    typeof candidate.serverUrl === 'string'
  );
}

// ============================================================================
// Challenge Generation and Validation
// ============================================================================

/**
 * Generate cryptographic challenge for QR code
 *
 * @returns Base64-encoded random challenge
 */
async function generateChallenge(): Promise<string> {
  const randomBytes = new Uint8Array(32); // 256 bits
  window.crypto.getRandomValues(randomBytes);
  return arrayBufferToBase64(randomBytes.buffer);
}

/**
 * Validate challenge from scanned QR code
 *
 * @param challenge - Challenge from QR code
 * @param sessionId - Session ID from QR code
 * @returns True if challenge is valid
 */
export function validateChallenge(
  challenge: string,
  sessionId: string,
): boolean {
  try {
    // Retrieve stored payload
    const storedPayload = getStoredQRCodePayload(sessionId);
    if (!storedPayload) {
      logger.warn('No stored payload found for session', { sessionId });
      return false;
    }

    const payload = JSON.parse(storedPayload) as QRCodePayload;

    // Verify challenge matches
    if (payload.challenge !== challenge) {
      logger.warn('Challenge mismatch', { sessionId });
      return false;
    }

    // Verify not expired
    if (payload.expiresAt < Date.now()) {
      logger.warn('Challenge expired', { sessionId });
      return false;
    }

    return true;
  } catch (error) {
    logger.error('Failed to validate challenge', error as Error);
    return false;
  }
}

// ============================================================================
// Session Storage (temporary in-memory store)
// ============================================================================

const qrCodeSessions = new Map<string, string>();

function storeQRCodePayload(sessionId: string, payload: string): void {
  qrCodeSessions.set(sessionId, payload);

  // Auto-cleanup after 10 minutes
  setTimeout(
    () => {
      qrCodeSessions.delete(sessionId);
    },
    10 * 60 * 1000,
  );
}

function getStoredQRCodePayload(sessionId: string): string | undefined {
  return qrCodeSessions.get(sessionId);
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Generate unique session ID
 */
function generateSessionId(): string {
  const randomBytes = new Uint8Array(16);
  window.crypto.getRandomValues(randomBytes);
  return Array.from(randomBytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Get server URL for WebSocket connection
 */
function getServerUrl(): string {
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}`;
  }
  return 'wss://trueid.locker.app'; // Fallback
}

/**
 * Convert ArrayBuffer to Base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i] ?? 0);
  }
  return btoa(binary);
}

/**
 * Get remaining time until QR code expires
 *
 * @param expiresAt - Expiration timestamp
 * @returns Remaining time in seconds
 */
export function getRemainingTime(expiresAt: number): number {
  return Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
}

/**
 * Format remaining time as MM:SS
 *
 * @param seconds - Remaining seconds
 * @returns Formatted time string
 */
export function formatRemainingTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}
